import * as React from 'react';

import {isNullOrEmpty} from '@developers/utils/String';

export interface FormHandle<T> {
  requestSubmit: () => void;
  resetForm: () => void;
  getFormData: () => T;
}

interface Props<T> {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onChange?: (fieldName: string, fieldValue: unknown, event?: unknown) => void;
  onSubmit?: (formData: T, event: React.SyntheticEvent<HTMLFormElement>) => void;
  handleRef?: React.Ref<FormHandle<T>>;
}

export default function Form<T = unknown>({onChange, onSubmit, className, style, children, handleRef}: Props<T>) {
  const formRef = React.useRef<HTMLFormElement>(null);

  function getFormData(): T {
    if (formRef.current == null) return {} as T;

    // Using a dirty dict, and then casting below to `T`. This is one of those rare
    // cases where making the code typesafe would not be worth it IMO (beckwith)
    const formData: Record<string, unknown> = {};
    for (const ele of formRef.current.elements) {
      const element = ele as HTMLInputElement;
      const {name, type, value} = element;

      if (isNullOrEmpty(name) || type === 'button' || type === 'submit' || type === 'reset') {
        continue;
      }
      if (type === 'checkbox') {
        formData[name] = element.checked;
      } else if (type === 'file') {
        formData[name] = element.files;
      } else if (type === 'text') {
        formData[name] = value === '' ? null : value;
      } else if (type !== 'radio') {
        formData[name] = value;
      } else if (type === 'radio' && !element.checked && !formData.hasOwnProperty(name)) {
        formData[name] = null;
      } else if (type === 'radio' && element.checked) {
        formData[name] = value;
      }
    }
    return formData as T;
  }

  const handleFormChange = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    if (onChange != null) {
      const target = event.currentTarget;
      onChange(target.name, target.value, event);
    }
  };

  const handleFormSubmit = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (onSubmit != null) {
      onSubmit(getFormData(), event);
    }
  };

  React.useImperativeHandle(handleRef, () => ({
    requestSubmit: () => formRef.current?.requestSubmit(),
    resetForm: () => formRef.current?.reset(),
    getFormData,
  }));

  return (
    <form ref={formRef} className={className} style={style} onChange={handleFormChange} onSubmit={handleFormSubmit}>
      {children}
    </form>
  );
}
